import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { DragIndicator, AltRoute, DeleteOutline, History } from '@mui/icons-material';
import useThemeStyles from '../../../../../../../../../hooks/useThemeStyles';
import { useProjectAccess } from '../../../../../../../../../contexts/ProjectAccessContext';

const SceneHeaderRow = ({
  scene,
  sceneIndex,
  onDragStart,
  onDragEnd,
  isDraggable = true,
  onBeginSplitPlacement,
  splitPlacementActive = false,
  onOpenSceneActions,
  onOpenHistory,
}) => {
  const { colors, isDark } = useThemeStyles();
  const { canEdit } = useProjectAccess();
  const allowDrag = isDraggable && canEdit;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        data-scene-header-interactive
        draggable={allowDrag}
        onDragStart={(e) => {
          if (!allowDrag) return;
          onDragStart(e, sceneIndex);
        }}
        onDragEnd={() => {
          if (!allowDrag) return;
          onDragEnd();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: allowDrag ? 'grab' : 'default',
          '&:active': {
            cursor: allowDrag ? 'grabbing' : 'default',
          },
          '&:hover': {
            '& .drag-indicator': {
              opacity: allowDrag ? 1 : 0.3,
            },
          },
        }}
      >
        <DragIndicator
          className="drag-indicator"
          sx={{
            fontSize: '1.2rem',
            color: isDark ? '#cbd5e1' : colors.text.secondary,
            opacity: allowDrag ? 0.6 : 0.3,
            transition: 'opacity 0.2s',
          }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          color: isDark ? '#cbd5e1' : colors.text.secondary,
          fontSize: '0.75rem',
          fontWeight: 'medium',
          userSelect: 'none',
        }}
      >
        {scene?.name?.trim() ? scene.name : 'Untitled scene'}
      </Typography>
      {(onBeginSplitPlacement || onOpenSceneActions || onOpenHistory) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          {onOpenHistory && (
            <Tooltip title="Version history">
              <IconButton
                data-scene-header-interactive
                size="small"
                aria-label="Version history"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenHistory(sceneIndex, scene);
                }}
                sx={{
                  color: colors.text.secondary,
                  opacity: 0.85,
                  '&:hover': { color: colors.interactive.primary },
                }}
              >
                <History sx={{ fontSize: '1.15rem' }} />
              </IconButton>
            </Tooltip>
          )}
          {onBeginSplitPlacement && canEdit && (
            <Tooltip
              title={splitPlacementActive ? 'Pick where to split below' : 'Split into new scene'}
            >
              <IconButton
                data-scene-header-interactive
                size="small"
                aria-label="Split scene"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onBeginSplitPlacement(sceneIndex);
                }}
                sx={{
                  color: splitPlacementActive ? colors.primary.main : colors.text.secondary,
                  opacity: splitPlacementActive ? 1 : 0.85,
                  '&:hover': { color: colors.primary.main },
                }}
              >
                <AltRoute sx={{ fontSize: '1.15rem' }} />
              </IconButton>
            </Tooltip>
          )}
          {onOpenSceneActions && canEdit && (
            <Tooltip title="Delete or merge scene">
              <IconButton
                data-scene-header-interactive
                size="small"
                aria-label="Delete or merge scene"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenSceneActions(sceneIndex, scene);
                }}
                sx={{
                  color: colors.text.secondary,
                  opacity: 0.85,
                  '&:hover': { color: colors.semantic.error },
                }}
              >
                <DeleteOutline sx={{ fontSize: '1.15rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

SceneHeaderRow.propTypes = {
  scene: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    wordCount: PropTypes.number,
  }).isRequired,
  sceneIndex: PropTypes.number.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  isDraggable: PropTypes.bool,
  onBeginSplitPlacement: PropTypes.func,
  splitPlacementActive: PropTypes.bool,
  onOpenSceneActions: PropTypes.func,
  onOpenHistory: PropTypes.func,
};

SceneHeaderRow.defaultProps = {
  isDraggable: true,
  splitPlacementActive: false,
  onBeginSplitPlacement: undefined,
  onOpenSceneActions: undefined,
  onOpenHistory: undefined,
};

export default SceneHeaderRow;
